import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CubeIcon } from '@newcore/icons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { InputField } from '@/components/forms/input-field';
import { LoginCard } from '@/components/login-card';
import { LoginButton } from '@/components/ui/login-button';
import { Page } from '@/components/ui/page';
import { useLanguage } from '@/contexts/language-context';
import { useLoginForm } from '@/hooks/forms/use-login-form';
import type { MfaFlowApiResponse } from '@/hooks/use-mfa-orchestrator.types';
import type { SignOnApp } from '@/services/apis/mfa-orchestrator.api';

type LoginUiNavigationState = { presetUsername?: string };

interface LoginUIProps {
  authId: string;
  onSuccessLogin: (result: MfaFlowApiResponse, username: string) => void;
  // Destination app for SP-initiated flows; null for IdP-initiated.
  spApp?: SignOnApp | null;
}

export function LoginUI({ authId, onSuccessLogin, spApp }: LoginUIProps) {
  const { t } = useLanguage();
  const location = useLocation();
  const presetUsername = (location.state as LoginUiNavigationState | null)
    ?.presetUsername;

  const { form, onSubmit, handleKeyPress, isSubmitting, getErrorMessage } =
    useLoginForm({
      authId,
      onSuccessLogin,
      initialUsername: presetUsername,
    });

  const { register, handleSubmit } = form;
  const [iconError, setIconError] = useState(false);

  return (
    <Page className='flex flex-col justify-center items-center gap-4'>
      <LoginCard>
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col items-center gap-1 text-center'>
            {spApp ? (
              <div
                className='flex items-center justify-center gap-1'
                data-testid='sign-in-to-app'
              >
                <Text
                  as='span'
                  size={TextSizes.MD}
                  weight={FontWeights.SEMIBOLD}
                  className='text-primary'
                >
                  {t('auth.signInTo')} {spApp.name}
                </Text>
                {spApp.iconUrl && (
                  <span className='flex items-center rounded-md border border-secondary p-1'>
                    {iconError ? (
                      <CubeIcon
                        aria-hidden
                        className='size-5 text-secondary'
                        data-testid='sp-app-icon-fallback'
                      />
                    ) : (
                      <img
                        src={spApp.iconUrl}
                        alt=''
                        className='size-5 object-contain'
                        data-testid='sp-app-icon'
                        onError={() => setIconError(true)}
                      />
                    )}
                  </span>
                )}
              </div>
            ) : (
              <Text
                as='h1'
                size={TextSizes.MD}
                weight={FontWeights.SEMIBOLD}
                className='text-primary'
              >
                {t('auth.loginToYourAccount')}
              </Text>
            )}
            <Text size={TextSizes.SM} className='text-secondary'>
              {t('auth.enterSparkAccountUsername')}
            </Text>
          </div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className='flex flex-col gap-6'
          >
            <InputField
              autoFocus
              data-testid='username-input'
              label={t('auth.username')}
              placeholder={t('auth.emailPlaceholder')}
              inputType='text'
              autoComplete='username'
              {...register('username')}
              onKeyPress={handleKeyPress}
              error={getErrorMessage()}
            />
            <LoginButton
              data-testid='sign-in-button'
              type='submit'
              size={Sizes.LG}
              className='w-full'
              isLoading={isSubmitting}
            >
              {isSubmitting ? t('auth.signingIn') : t('auth.next')}
            </LoginButton>
          </form>
        </div>
      </LoginCard>
    </Page>
  );
}
